import { Grid } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import GenericTabs from '../../components/genericTabs/genericTabs'
import PositionsContainer from './positionsContainer'
import OrdersContainer from './ordersContainer'
import { navItems, OrdersNavButtons, PositionNavButtons, positionsNavItems } from '../../components/postionsWidget'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { useSelector } from 'react-redux'
import { selectSimulatorDate } from '../../selectors/simulator'
import GenericTabsSmall from '../../components/genericTabs/GenericTabsSmall'
import { ExtraSmallButton } from '../../components/custom'
import { createStringDateFromStamp, createStringDateFromStampWithTime } from '../../util/tradePage'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { downloadCSV } from '../../util/screener'

enum PositionOrderNavButtons {
    POSITIONS = 'POSITIONS',
    ORDERS = 'ORDERS'
}

export const simulatorNavItems = [
    {
        key: PositionOrderNavButtons.POSITIONS,
        label: 'Positions'
    },
    {
        key: PositionOrderNavButtons.ORDERS,
        label: 'Orders'
    }
]

type Props = {}

const PositionsAndOrdersWrapper = (props: Props) => {
    const [viewType, setViewType] = useState(PositionOrderNavButtons.POSITIONS)
    const [positionViewType, setPositionViewType] = useState(OrdersNavButtons.ALL)
    const { orders, ordersPrices } = useCombineBasketAnalyzeAndLegOrders()
    const timeStamp = useSelector(selectSimulatorDate)
    const [positionTableViewType, setPositionTableViewType] = useState(PositionNavButtons.PNL)

    const renderOrders = useMemo(() => {
        const obj = orders.map((order, i) => {
            return {
                order,
                orderPrice: ordersPrices[i]
            }
        }).filter((o) => ((o.order.timeStamp ?? 0) <= timeStamp) && o.order.traded).sort((a, b) => (a.order.timeStamp ?? 0) - (b.order.timeStamp ?? 0))
        return obj
    }, [timeStamp, orders, ordersPrices])

    const downloadOrdersCSV = useCallback(async () => {
        const data = await Promise.all(renderOrders.map(async (order)=>{
            const underlyingSecurity = await getSecurityAsync(getSecurityKey(order.order.security?.exchange ?? 0, order.order.security?.underlyingSecurityCode ?? ''))
            return {
                Symbol: `${underlyingSecurity?.symbol ?? ''} ${createStringDateFromStamp(order.order.security?.expiry?.expiry ?? 0)} ${order.order.contractType === 'OPTION' ? order.order.strike / (order.order.security?.priceMultiplier ?? 100) : ''} ${order.order.contractType === 'OPTION' ? order.order.callput === 'call' ? 'CE' : 'PE' : ''}`,
                'Order Time': createStringDateFromStampWithTime(order.order.timeStamp??0),
                'Transaction Type': order.order.type,
                Quantity: order.order.quantity * (order.order.security?.lotSize??1),
                Price: order.orderPrice?.price
            }
        }))
        downloadCSV(data, 'simulation data')
    }, [renderOrders])

    return (
        <Grid container justifyContent={'space-between'}>
            {
                orders.filter(o => ((o.timeStamp ?? 0) <= timeStamp)).length === 0 ? null : <>
                    <Grid item style={{ padding: '10px', display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <GenericTabs<PositionOrderNavButtons>
                            items={simulatorNavItems}
                            onChange={(val) => {
                                if (val) setViewType(val)
                            }}
                            value={viewType}
                            tabProps={{ sx: { minWidth: 60 } }}
                            sx={{ height: 32, pr: 1 }}
                        />
                        {
                            viewType === PositionOrderNavButtons.POSITIONS ?
                                <>
                                    <GenericTabs<OrdersNavButtons>
                                        items={positionsNavItems}
                                        onChange={(val) => {
                                            if (val) setPositionViewType(val)
                                        }}
                                        value={positionViewType}
                                        tabProps={{ sx: { minWidth: 60 } }}
                                        sx={{ height: 32 }}
                                    />
                                    <GenericTabsSmall<PositionNavButtons>
                                        items={navItems}
                                        onChange={(val) => {
                                            if (val) setPositionTableViewType(val)
                                        }}
                                        value={positionTableViewType}
                                        tabProps={{ sx: { minWidth: 60 } }}
                                    />
                                </> : <ExtraSmallButton onClick={downloadOrdersCSV}>Download csv</ExtraSmallButton>
                        }
                    </Grid>
                    <Grid item xs={12} style={{ padding: '10px' }}>
                        {
                            viewType === PositionOrderNavButtons.POSITIONS ?
                                <PositionsContainer showAll={positionViewType === OrdersNavButtons.ALL} showGreeks={positionTableViewType === PositionNavButtons.GREEKS} /> :
                                <OrdersContainer />
                        }
                    </Grid>
                </>
            }
        </Grid >
    )
}

export default PositionsAndOrdersWrapper